import { Photo } from "@/components/Photo";
import { SITE } from "@/lib/site";
import footerBackgroundVariants from "@/assets/footer-bg.webp?w=640;960;1280;1920;2301&format=webp&as=meta";
import { responsiveImage } from "@/lib/responsiveImage";
import anima from "@/assets/partners/anima.svg";
import cliffHouse from "@/assets/partners/cliff-house.svg";
import fairmont from "@/assets/partners/fairmont.svg";
import hilton from "@/assets/partners/hilton.svg";
import huntington from "@/assets/partners/huntington.svg";
import hyatt from "@/assets/partners/hyatt.svg";
import royalton from "@/assets/partners/royalton.svg";
import sirenis from "@/assets/partners/sirenis.svg";
import sugarBeach from "@/assets/partners/sugar-beach.svg";
import viceroy from "@/assets/partners/viceroy.svg";
import wyndham from "@/assets/partners/wyndham.svg";
import { Logo } from "@/components/Logo";

// Heights are the Figma export heights; one scale keeps every logo's drawn size relative to the others.
const LOGO_SCALE_REM = 0.3 / 16;
const PARTNERS = [
  { src: huntington, alt: "Huntington", height: 160 },
  { src: hilton, alt: "Hilton", height: 72 },
  { src: sugarBeach, alt: "Sugar Beach", height: 60 },
  { src: sirenis, alt: "Sirenis", height: 60 },
  { src: viceroy, alt: "Viceroy", height: 60 },
  { src: hyatt, alt: "Hyatt", height: 80 },
  { src: cliffHouse, alt: "Cliff House Maine", height: 96 },
  { src: anima, alt: "Ánima Village", height: 88 },
  { src: wyndham, alt: "Wyndham Hotels & Resorts", height: 80 },
  { src: fairmont, alt: "Fairmont Hotels & Resorts", height: 96 },
  { src: royalton, alt: "Royalton Luxury Resorts", height: 96 },
];

const LINKS = [
  { label: "Meet Somo", href: `${SITE}/meet-somo` },
  { label: "About", href: `${SITE}/about` },
  { label: "Get A Quote", href: `${SITE}/get-a-quote` },
  { label: "Contact", href: `${SITE}/contact` },
];

const footerBackground = responsiveImage(footerBackgroundVariants);

export function Footer() {
  return (
    // Pinned only on screens tall enough to show all of it; a taller footer would
    // pin with its top permanently hidden behind the page.
    <footer
      data-tone="dark"
      id="footer"
      className="relative overflow-hidden bg-inverse text-inverse tall:sticky tall:bottom-0"
    >
      <Photo
        {...footerBackground}
        sizes="100vw"
        alt=""
        tone="dark"
        className="absolute inset-0"
        imgClassName="object-bottom"
      />
      <div className="footer-glow absolute inset-0 opacity-30" />
      <div className="relative flex flex-col gap-12 pb-16 pt-16 md:items-center md:gap-14">
        <PartnerMarquee />
        <div className="mt-8 flex flex-col gap-20 px-8 md:mt-0 md:items-center md:gap-16 md:px-0">
          <div className="flex flex-col gap-12">
            <Logo className="size-16" />
            <div className="flex flex-col gap-4 text-shadow-soft md:hidden">
              <p className="text-2xl leading-tight">
                Building Intelligence.
                <br />
                For Hospitality.
              </p>
              <p className="text-base leading-title tracking-body">
                Somo brings amazing lighting and climate controls to your hotel.
                Creating the best guest experience and increasing profits.
              </p>
            </div>
          </div>
          <nav className="grid auto-cols-fr grid-flow-col grid-rows-2 gap-y-6 text-base leading-normal text-shadow-soft md:flex md:flex-wrap md:justify-center md:gap-x-20 md:gap-y-4 md:px-6">
            {LINKS.map((link) => (
              <a
                key={link.label}
                href={link.href}
                className="transition-opacity hover:opacity-70"
              >
                {link.label}
              </a>
            ))}
          </nav>
        </div>
        <div className="flex flex-col items-start gap-4 px-8 text-xs md:flex-row md:flex-wrap md:items-center md:justify-center md:px-6 leading-normal text-inverse-secondary text-shadow-soft">
          <a href="#" className="hover:text-inverse">
            Privacy Policy
          </a>
          <p className="leading-loose md:leading-normal">
            © 2026 Somo AI. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

function PartnerMarquee() {
  return (
    <div
      className="w-full max-w-screen-2xl overflow-hidden [mask-image:linear-gradient(to_right,transparent,black_15%,black_85%,transparent)]"
      aria-label="Hospitality partners"
    >
      <div className="flex w-max animate-marquee">
        {[0, 1].map((copy) => (
          <div
            key={copy}
            aria-hidden={copy === 1}
            className="flex shrink-0 items-center gap-10 pr-10 md:gap-32 md:pr-32"
          >
            {[...PARTNERS, ...PARTNERS].map((partner, index) => (
              <img
                loading="lazy"
                decoding="async"
                key={`${partner.alt}-${index}`}
                src={partner.src}
                alt={index < PARTNERS.length ? partner.alt : ""}
                style={{ height: `${partner.height * LOGO_SCALE_REM}rem` }}
                className="w-auto object-contain"
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
